import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { findSimilarClients, normalize } from './ClientsPage';
import { mismaPersona } from '../utils/nameMatch';
import { companiaCanonica, listaCompanias } from '../utils/companies';
import { pedirJSONIA } from '../utils/aiClient';
import { format, parseISO, differenceInDays } from 'date-fns';
import { fmtPatron } from '../utils/dates';
import { es } from 'date-fns/locale';
import {
  Plus, Search, X, Send, Paperclip, Bell,
  AlertTriangle, Edit2, Trash2, Sparkles, FileText, CheckCircle, Loader,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';

const ESTADOS = {
  open:       { label: 'Abierto',    color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  inprogress: { label: 'En curso',   color: 'var(--amber)', bg: 'var(--amber-dim)' },
  waiting:    { label: 'En espera',  color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  resolved:   { label: 'Resuelto',   color: 'var(--accent)', bg: 'var(--accent-dim)' },
  closed:     { label: 'Cerrado',    color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.05)' },
};

const PRIORIDADES = {
  low:    { label: 'Baja',  color: 'var(--accent)' },
  medium: { label: 'Media', color: 'var(--amber)' },
  high:   { label: 'Alta',  color: 'var(--red)' },
};

const TIPOS = [
  'Robo / Hurto', 'Accidente de tránsito', 'Daños materiales', 'Incendio',
  'Responsabilidad civil', 'Granizo', 'Inundación', 'Rotura de vidrios',
  'Accidente personal', 'Otro',
];

// ── PDF con IA: extrae datos de denuncia ─────────────────────
function ClaimPdfUpload({ onDataExtracted, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [modo, setModo] = useState('pdf'); // 'pdf' | 'texto'
  const [texto, setTexto] = useState('');
  const fileRef = useRef();

  // Prompt compartido entre PDF y texto
  const PROMPT_EXTRACCION = `Respondé ÚNICAMENTE con un objeto JSON válido, sin texto antes ni después, sin backticks, sin markdown. El JSON debe tener exactamente esta estructura:
{"claimNumber":"","policyNumber":"","claimDate":"","occurrenceDate":"","type":"","company":"","clientName":"","clientPhone":"","clientEmail":"","clientDni":"","clientAddress":"","vehicleBrand":"","vehicleModel":"","vehicleYear":"","vehiclePlate":"","vehicleChasis":"","damages":"","location":"","deductible":"","description":"","thirdParties":""}

Instrucciones:
- claimNumber: número de siniestro (ej: "12312/2026" o "22505")
- policyNumber: número de póliza
- claimDate: fecha de denuncia en formato YYYY-MM-DD
- occurrenceDate: fecha del siniestro/ocurrencia en formato YYYY-MM-DD
- type: tipo de siniestro en español (ej: "Accidente de tránsito", "Robo / Hurto", "Daños materiales")
- company: nombre de la compañía ASEGURADORA que gestiona el siniestro (ej: Sura, BSE, Mapfre, Porto Seguro, Sancor, Berkley, SBI, San Cristóbal, Surco, MetLife, Zurich, HDI). OJO: NO es el nombre del asegurado ni del cliente, aunque el cliente sea una empresa (SA, SRL). Si no se identifica una aseguradora conocida, dejalo vacío ""
- clientName: nombre completo del asegurado (Nombre + Apellido)
- clientPhone: teléfono principal del asegurado
- clientEmail: email del asegurado
- clientDni: CI o documento de identidad del asegurado
- clientAddress: dirección del asegurado
- vehicleBrand: marca del vehículo asegurado
- vehicleModel: modelo del vehículo asegurado
- vehicleYear: año del vehículo
- vehiclePlate: matrícula o patente del vehículo asegurado
- vehicleChasis: número de chasis
- damages: descripción de daños al vehículo asegurado
- location: dirección donde ocurrió el siniestro
- deductible: monto del deducible o franquicia
- description: declaración completa del accidente
- thirdParties: terceros involucrados (nombre, matrícula, seguro)

Si un campo no aparece, dejalo como string vacío "".`;

  const callIA = async (contentBlocks, errPrefix) => {
    setError(''); setLoading(true); setPreview(null);
    try {
      const json = await pedirJSONIA({ content: contentBlocks, maxTokens: 4000, esperado: 'object' });
      // La IA a veces pone el nombre del cliente-empresa (ej "SAMELA SA") como
      // aseguradora. Se valida contra la lista canónica: si no es una compañía
      // conocida del mercado, queda vacío y se carga a mano.
      if (json && typeof json === 'object') json.company = companiaCanonica(json.company) || '';
      setPreview(json);
    } catch (err) {
      setError(errPrefix + err.message);
    }
    setLoading(false);
  };

  const handleTexto = async () => {
    if (!texto.trim()) { setError('Pegá el texto del mail o la denuncia primero.'); return; }
    await callIA([
      { type: 'text', text: `Analizá el siguiente texto (puede ser un mail o una denuncia de siniestro de seguro) y extraé todos los datos del siniestro.\n\n${PROMPT_EXTRACCION}\n\n--- TEXTO ---\n${texto}` }
    ], 'Error al procesar el texto: ');
  };

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f || !f.name.toLowerCase().endsWith('.pdf')) { setError('Solo se aceptan archivos PDF'); return; }
    setFile(f);
    const base64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(',')[1]);
      r.onerror = () => rej(new Error('Error al leer el archivo'));
      r.readAsDataURL(f);
    });
    await callIA([
      { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
      { type: 'text', text: `Analizá este comprobante de denuncia de siniestro de seguro y extraé todos los datos.\n\n${PROMPT_EXTRACCION}` }
    ], 'Error al procesar el PDF: ');
  };

  const handleConfirm = () => {
    if (!preview) return;
    onDataExtracted(preview);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={18} color="var(--accent)" />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Cargar denuncia con IA</h2>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Subí el comprobante de denuncia o pegá el texto del mail, y la IA extrae automáticamente todos los datos para crear el siniestro.
          </p>

          {/* Selector de modo */}
          <div style={{ display: 'flex', gap: 6, background: 'var(--bg-input)', padding: 4, borderRadius: 10 }}>
            <button onClick={() => { setModo('pdf'); setError(''); setPreview(null); }}
              className="btn btn-sm" style={{ flex: 1, background: modo === 'pdf' ? 'var(--accent-dim)' : 'transparent', color: modo === 'pdf' ? 'var(--accent)' : 'var(--text-secondary)', border: 'none' }}>
              <FileText size={14} /> Subir PDF
            </button>
            <button onClick={() => { setModo('texto'); setError(''); setPreview(null); setFile(null); }}
              className="btn btn-sm" style={{ flex: 1, background: modo === 'texto' ? 'var(--accent-dim)' : 'transparent', color: modo === 'texto' ? 'var(--accent)' : 'var(--text-secondary)', border: 'none' }}>
              <Sparkles size={14} /> Pegar texto / mail
            </button>
          </div>

          {modo === 'pdf' && (
          <div onClick={() => fileRef.current?.click()} style={{
            border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 12, padding: 28, textAlign: 'center', cursor: 'pointer',
            background: file ? 'var(--accent-dim)' : 'var(--bg-input)',
          }}>
            <input ref={fileRef} type="file" onClick={e => { e.target.value = null; }} accept=".pdf" onChange={handleFile} style={{ display: 'none' }} />
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--accent)' }}>
                <Loader size={28} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 13 }}>Analizando denuncia...</span>
              </div>
            ) : file ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <FileText size={28} color="var(--accent)" />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{file.name}</span>
                {preview && <span style={{ fontSize: 12, color: 'var(--accent)' }}>✅ Datos extraídos correctamente</span>}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Sparkles size={28} />
                <span style={{ fontSize: 13 }}>Clic para seleccionar el PDF de denuncia</span>
                <span style={{ fontSize: 11 }}>Compatible con Sura, Berkley, BSE y otras compañías</span>
              </div>
            )}
          </div>
          )}

          {modo === 'texto' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={8}
              placeholder="Pegá acá el mail o el texto de la denuncia del siniestro..."
              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
            <button onClick={handleTexto} disabled={loading} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
              {loading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analizando...</> : <><Sparkles size={14} /> Extraer datos del texto</>}
            </button>
          </div>
          )}

          {error && <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)' }}>{error}</div>}

          {preview && (
            <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Vista previa de datos extraídos</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  ['Nro siniestro', preview.claimNumber],
                  ['Nro póliza', preview.policyNumber],
                  ['Compañía', preview.company],
                  ['Tipo', preview.type],
                  ['Asegurado', preview.clientName],
                  ['Teléfono', preview.clientPhone],
                  ['Email', preview.clientEmail],
                  ['CI', preview.clientDni],
                  ['Vehículo', [preview.vehicleBrand, preview.vehicleModel, preview.vehicleYear].filter(Boolean).join(' ')],
                  ['Matrícula', preview.vehiclePlate],
                  ['Fecha siniestro', preview.occurrenceDate],
                  ['Lugar', preview.location],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 12 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{k}: </span>
                    <span style={{ fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
              {preview.damages && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)' }}>Daños: </span><span style={{ fontWeight: 500 }}>{preview.damages}</span></div>}
              {preview.thirdParties && <div style={{ fontSize: 12 }}><span style={{ color: 'var(--text-muted)' }}>Terceros: </span><span style={{ fontWeight: 500 }}>{preview.thirdParties}</span></div>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          {preview && (
            <button onClick={handleConfirm} className="btn btn-primary">
              <CheckCircle size={14} /> Crear siniestro con estos datos
            </button>
          )}
          {!preview && (file || texto) && !loading && (
            <button onClick={() => { setFile(null); setPreview(null); setTexto(''); }} className="btn btn-secondary">
              Limpiar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal nuevo/editar ────────────────────────────────────────
function ClaimModal({ claim, prefill, onClose }) {
  const { addClaim, updateClaim, clients, policies, currentUser, users } = useApp();
  const isEdit = !!claim;
  const today = format(new Date(), 'yyyy-MM-dd');

  // Buscar el cliente del siniestro. Orden de confiabilidad:
  //  1) Por número de PÓLIZA: si la póliza existe, su dueño es el cliente. Es lo
  //     más confiable y no depende de cómo esté escrito el nombre.
  //  2) Por CI/RUT exacto.
  //  3) Por teléfono.
  //  4) Por nombre, usando mismaPersona() (solapamiento alto; "Juan" suelto NO
  //     alcanza). Antes bastaba una palabra en común y asociaba mal (ej.
  //     "MALATES VERA JUAN" caía en "PAZ, JUAN CARLOS").
  let prefillClient = null;
  if (prefill && !isEdit) {
    if (prefill.policyNumber) {
      const pol = policies.find(p => p.number?.trim().toLowerCase() === prefill.policyNumber?.trim().toLowerCase());
      if (pol) prefillClient = clients.find(c => c.id === pol.clientId) || null;
    }
    if (!prefillClient) {
      // DNI/teléfono valen como match SOLO si el nombre también es compatible.
      // Un DNI o teléfono compartido (familias, número de la corredora repetido,
      // datos mal cargados) no debe asociar el siniestro a otra persona.
      const dniR = (prefill.clientDni || '').replace(/[\s.]/g,'');
      const phoneR = (prefill.clientPhone || '').replace(/\s/g,'');
      const dniOk = dniR.length >= 6;
      const phoneOk = phoneR.length >= 7;
      const nombreCompatible = (c) => !prefill.clientName || mismaPersona(c.name, prefill.clientName);
      prefillClient = clients.find(c => {
        const matchDni = dniOk && (c.dni || '').replace(/[\s.]/g,'') === dniR && nombreCompatible(c);
        const matchPhone = phoneOk && (c.phone || '').replace(/\s/g,'') === phoneR && nombreCompatible(c);
        const matchName = prefill.clientName && mismaPersona(c.name, prefill.clientName);
        return matchDni || matchPhone || matchName;
      }) || null;
    }
  }

  const prefillPolicy = prefill && prefillClient ? policies.find(p =>
    p.clientId === prefillClient.id && (
      (prefill.policyNumber && p.number?.trim().toLowerCase() === prefill.policyNumber?.trim().toLowerCase()) ||
      (prefill.vehiclePlate && p.assetData?.plate?.toLowerCase() === prefill.vehiclePlate?.toLowerCase())
    )
  ) : null;

  const [form, setForm] = useState(claim || {
    title: prefill?.title || '',
    type: prefill?.type || '',
    clientId: prefillClient?.id || '',
    policyId: prefillPolicy?.id || '',
    status: 'open',
    priority: 'medium',
    description: prefill?.description || '',
    occurrenceDate: prefill?.occurrenceDate || today,
    reminder: '',
    assignedTo: prefill?.assignedTo || '',
    claimNumber: prefill?.claimNumber || '',
    company: prefill?.company || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const clientPolicies = policies.filter(p => p.clientId === form.clientId);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    const dataToSave = { ...form };
    // Si no hay cliente seleccionado pero hay nombre libre, guardarlo en descripción
    if (!dataToSave.clientId && dataToSave.clientNameFree) {
      dataToSave.description = [
        `Asegurado: ${dataToSave.clientNameFree}`,
        dataToSave.policyNumberFree ? `Póliza: ${dataToSave.policyNumberFree}` : '',
        dataToSave.description || '',
      ].filter(Boolean).join('\n');
    }
    if (isEdit) await updateClaim(claim.id, dataToSave);
    else await addClaim({ ...dataToSave, createdBy: currentUser?.name });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>
            {isEdit ? 'Editar siniestro' : 'Nuevo siniestro'}
          </h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Título *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} required
                  placeholder="Ej: Choque — VW T-Cross SCB5817" />
              </div>
              <div className="form-group">
                <label className="form-label">N° Siniestro (compañía)</label>
                <input value={form.claimNumber || ''} onChange={e => set('claimNumber', e.target.value)}
                  placeholder="Ej: 12312/2026" />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Tipo de siniestro *</label>
                <select value={form.type} onChange={e => set('type', e.target.value)} required>
                  <option value="">Seleccionar...</option>
                  {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha del siniestro</label>
                <input type="date" value={form.occurrenceDate} onChange={e => set('occurrenceDate', e.target.value)} />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Cliente <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
                <select value={form.clientId} onChange={e => { set('clientId', e.target.value); set('policyId', ''); }}>
                  <option value="">Sin cliente registrado aún...</option>
                  {[...clients].sort((a,b) => a.name?.localeCompare(b.name) || 0).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {!form.clientId && (
                  <input
                    value={form.clientNameFree || ''}
                    onChange={e => set('clientNameFree', e.target.value)}
                    placeholder="Nombre del asegurado (si no está en el sistema)"
                    style={{ marginTop: 6, fontSize: 13 }}
                  />
                )}
                {!form.clientId && (() => {
                  const texto = form.clientNameFree || form.title || '';
                  const sugeridos = texto.trim().length >= 3 ? findSimilarClients(texto, clients, 0.55) : [];
                  if (sugeridos.length === 0) return null;
                  return (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--amber-dim)', border: '1px solid var(--amber)', borderRadius: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600, marginBottom: 6 }}>
                        ¿Es alguno de estos clientes?
                      </div>
                      {sugeridos.map(({ client: c }) => (
                        <button type="button" key={c.id}
                          onClick={() => { set('clientId', c.id); set('clientNameFree', ''); }}
                          className="btn btn-secondary btn-sm"
                          style={{ marginRight: 6, marginBottom: 4, fontSize: 12 }}>
                          + {c.name}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="form-group">
                <label className="form-label">Póliza asociada <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opcional)</span></label>
                <select value={form.policyId} onChange={e => set('policyId', e.target.value)}>
                  <option value="">Sin póliza específica</option>
                  {clientPolicies.map(p => <option key={p.id} value={p.id}>{p.number} — {p.company}</option>)}
                </select>
                {!form.clientId && (
                  <input
                    value={form.policyNumberFree || ''}
                    onChange={e => set('policyNumberFree', e.target.value)}
                    placeholder="N° de póliza (si no está en el sistema)"
                    style={{ marginTop: 6, fontSize: 13 }}
                  />
                )}
              </div>
            </div>
            {!form.policyId && (
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Compañía</label>
                  <input value={form.company || ''} onChange={e => set('company', e.target.value)}
                    list="claim-companies-list" placeholder="Ej: Sura, BSE, Berkley…" autoComplete="off" />
                  <datalist id="claim-companies-list">
                    {listaCompanias().map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
            )}
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Prioridad</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {Object.entries(PRIORIDADES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Asignado a</label>
                <select value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">🔔 Recordatorio</label>
                <input type="date" value={form.reminder || ''} onChange={e => set('reminder', e.target.value)} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Aparecerá como alerta cuando llegue la fecha</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                rows={3} placeholder="Detallá lo ocurrido, daños, circunstancias..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">
              {isEdit ? 'Guardar cambios' : 'Crear siniestro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Panel lateral de detalle ──────────────────────────────────
function ClaimDetail({ claimId, onClose }) {
  const { claims, updateClaim, deleteClaim, clients, policies, currentUser } = useApp();
  const claim = claims.find(c => c.id === claimId);
  const [newUpdate, setNewUpdate] = useState('');
  const [newAttach, setNewAttach] = useState('');
  const [editModal, setEditModal] = useState(false);

  if (!claim) return null;

  const client = clients.find(c => c.id === claim.clientId) || {};
  const policy = policies.find(p => p.id === claim.policyId);
  const estado = ESTADOS[claim.status] || ESTADOS.open;
  const prioridad = PRIORIDADES[claim.priority] || PRIORIDADES.medium;
  const today = new Date().toISOString().split('T')[0];
  const hasReminderAlert = claim.reminder && claim.reminder <= today && claim.status !== 'closed';

  const sendUpdate = async () => {
    if (!newUpdate.trim()) return;
    const update = {
      id: Date.now().toString(),
      text: newUpdate.trim(),
      author: currentUser?.name || 'Agente',
      date: new Date().toISOString(),
    };
    await updateClaim(claim.id, { updates: [...(claim.updates || []), update] });
    setNewUpdate('');
  };

  const addAttach = async () => {
    if (!newAttach.trim()) return;
    const att = { id: Date.now().toString(), name: newAttach.trim(), date: new Date().toISOString(), addedBy: currentUser?.name || 'Agente' };
    await updateClaim(claim.id, { attachments: [...(claim.attachments || []), att] });
    setNewAttach('');
  };

  const removeAttach = async (id) => {
    await updateClaim(claim.id, { attachments: (claim.attachments || []).filter(a => a.id !== id) });
  };

  const handleDelete = async () => {
    if (window.confirm('¿Eliminar este siniestro? Esta acción no se puede deshacer.')) {
      await deleteClaim(claim.id);
      onClose();
    }
  };

  return (
    <div className="side-panel" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 240, zIndex: 1000, display: 'flex', height: '100vh' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
      <div style={{
        position: 'relative', marginLeft: 'auto', width: '100%',
        background: 'var(--bg-card)', borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh', overflowY: 'auto',
      }}>
        {/* Header fijo */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <span className="badge" style={{ background: estado.bg, color: estado.color }}>{estado.label}</span>
                <span className="badge" style={{ background: prioridad.color + '20', color: prioridad.color }}>{prioridad.label} prioridad</span>
                {hasReminderAlert && <span className="badge badge-amber">🔔 Recordatorio vencido</span>}
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{claim.title}</h2>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {client.name} {policy && `· ${policy.number}`} · {claim.type}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditModal(true)}><Edit2 size={13} /></button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}><Trash2 size={13} /></button>
              <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
            </div>
          </div>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Info rápida */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['👤 Cliente', client.name, client.phone],
              ['📄 Póliza', policy ? `${policy.number}` : '—', policy?.company],
              ['🏢 Compañía', policy?.company || companiaCanonica(claim.company) || '⚠ Cargar compañía', policy?.company || companiaCanonica(claim.company) ? null : (claim.company ? `Sin reconocer: "${claim.company}"` : 'Falta cargar')],
              ['📅 Fecha siniestro', claim.occurrenceDate ? fmtPatron(claim.occurrenceDate, 'dd/MM/yyyy') : '—', null],
              ['👤 Asignado a', claim.assignedTo || '—', claim.createdBy ? `Creado por: ${claim.createdBy}` : null],
              ['📅 Abierto', claim.createdAt ? format(parseISO(claim.createdAt), "d MMM yyyy", { locale: es }) : '—', null],
              claim.reminder ? ['🔔 Recordatorio', fmtPatron(claim.reminder, 'dd/MM/yyyy'), hasReminderAlert ? '⚠️ Fecha vencida' : null] : null,
            ].filter(Boolean).map(([k, v, sub]) => (
              <div key={k} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, fontFamily: 'var(--font-display)', fontWeight: 600 }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
              </div>
            ))}
          </div>

          {/* Descripción */}
          {claim.description && (
            <div className="card">
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Descripción</div>
              <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0 }}>{claim.description}</p>
            </div>
          )}

          {/* Cambiar estado */}
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Estado del caso</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(ESTADOS).map(([k, v]) => (
                <button key={k} onClick={() => updateClaim(claim.id, { status: k })} className="btn btn-sm" style={{
                  background: claim.status === k ? v.bg : 'transparent',
                  color: claim.status === k ? v.color : 'var(--text-muted)',
                  border: `1px solid ${claim.status === k ? v.color + '60' : 'var(--border)'}`,
                  fontWeight: claim.status === k ? 700 : 400,
                }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actualizaciones */}
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Actualizaciones y comentarios ({(claim.updates || []).length})
            </div>
            {/* Lista en orden inverso */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {(claim.updates || []).length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>Sin actualizaciones — sé el primero en comentar</p>
              )}
              {[...(claim.updates || [])].reverse().map(u => (
                <div key={u.id} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-display)' }}>
                      👤 {u.author}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {format(new Date(u.date), "d MMM yyyy · HH:mm", { locale: es })}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{u.text}</p>
                </div>
              ))}
            </div>
            {/* Input */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={newUpdate} onChange={e => setNewUpdate(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendUpdate(); }}
                placeholder="Escribí una actualización... (⌘+Enter para enviar)"
                rows={2} style={{ flex: 1, resize: 'none', fontSize: 13 }} />
              <button className="btn btn-primary" onClick={sendUpdate} disabled={!newUpdate.trim()}
                style={{ padding: '10px 14px', flexShrink: 0 }}>
                <Send size={14} />
              </button>
            </div>
          </div>

          {/* Cotizaciones y documentos */}
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, fontFamily: 'var(--font-display)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Cotizaciones y documentos ({(claim.attachments || []).length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {(claim.attachments || []).length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin documentos adjuntos</p>
              )}
              {(claim.attachments || []).map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-input)', borderRadius: 8, padding: '8px 12px' }}>
                  <Paperclip size={13} color="var(--accent)" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13 }}>{a.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.addedBy} · {format(new Date(a.date), 'd MMM', { locale: es })}</span>
                  <button className="btn btn-secondary btn-sm" style={{ padding: '3px 7px' }} onClick={() => removeAttach(a.id)}><X size={11} /></button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newAttach} onChange={e => setNewAttach(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addAttach()}
                placeholder="Nombre del documento o cotización (Enter para agregar)"
                style={{ flex: 1, fontSize: 13 }} />
              <button className="btn btn-secondary btn-sm" onClick={addAttach} disabled={!newAttach.trim()}>
                <Plus size={13} />
              </button>
            </div>
          </div>

        </div>
      </div>

      {editModal && <ClaimModal claim={claim} onClose={() => setEditModal(false)} />}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function ClaimsPage() {
  const { claims, clients, policies } = useApp();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [newModal, setNewModal] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const today = new Date().toISOString().split('T')[0];

  // Enlace directo desde un email: /claims?id=XXX abre ese siniestro.
  // Se limpia el parámetro de la URL para que un refresh no lo reabra.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('id');
    if (id && claims.some(c => c.id === id)) {
      setSelectedId(id);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [claims]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const reminders = claims.filter(c =>
    c.reminder && c.reminder <= today && c.status !== 'closed' && c.status !== 'resolved'
  );

  const filtered = useMemo(() => {
    let list = claims.filter(c => {
      const client = clients.find(cl => cl.id === c.clientId) || {};
      const q = normalize(search);
      const matchSearch = !search ||
        normalize(c.title).includes(q) ||
        normalize(client.name).includes(q) ||
        normalize(c.type).includes(q) ||
        normalize(c.assignedTo).includes(q);
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchPriority = filterPriority === 'all' || c.priority === filterPriority;
      // Ocultar cerrados salvo que el filtro pida explícitamente "closed"
      const ocultarCerrado = c.status === 'closed' && filterStatus !== 'closed';
      return matchSearch && matchStatus && matchPriority && !ocultarCerrado;
    });

    return [...list].sort((a, b) => {
      let vA, vB;
      const prioOrder = { high: 0, medium: 1, low: 2 };
      const statusOrder = { open: 0, in_progress: 1, waiting: 2, resolved: 3, closed: 4 };
      switch (sortBy) {
        case 'priority': vA = prioOrder[a.priority] ?? 1; vB = prioOrder[b.priority] ?? 1; break;
        case 'status': vA = statusOrder[a.status] ?? 9; vB = statusOrder[b.status] ?? 9; break;
        case 'reminder': vA = a.reminder || '9999'; vB = b.reminder || '9999'; break;
        case 'client': {
          vA = (clients.find(c => c.id === a.clientId)?.name || '').toLowerCase();
          vB = (clients.find(c => c.id === b.clientId)?.name || '').toLowerCase(); break;
        }
        default: vA = a.createdAt || ''; vB = b.createdAt || '';
      }
      if (vA < vB) return sortDir === 'asc' ? -1 : 1;
      if (vA > vB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [claims, clients, search, filterStatus, filterPriority, sortBy, sortDir]);

  const SortTh = ({ field, children }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {children}
        {sortBy !== field && <ArrowUpDown size={11} style={{ color: 'var(--text-muted)' }} />}
        {sortBy === field && sortDir === 'asc' && <ArrowUp size={11} style={{ color: 'var(--accent)' }} />}
        {sortBy === field && sortDir === 'desc' && <ArrowDown size={11} style={{ color: 'var(--accent)' }} />}
      </span>
    </th>
  );

  const counts = Object.fromEntries(Object.keys(ESTADOS).map(k => [k, claims.filter(c => c.status === k).length]));

  const handlePdfData = (data) => {
    // Construir prefill para el modal de nuevo siniestro
    const title = [data.clientName, data.vehicleBrand, data.vehicleModel, data.vehiclePlate].filter(Boolean).join(' — ');
    setPrefill({
      title: title || `Siniestro ${data.claimNumber || ''}`,
      type: data.type || 'Accidente de tránsito',
      status: 'open',
      priority: 'medium',
      occurrenceDate: data.occurrenceDate || data.claimDate || '',
      description: [
        data.description ? `Declaración: ${data.description}` : '',
        data.damages ? `Daños: ${data.damages}` : '',
        data.location ? `Lugar: ${data.location}` : '',
        data.deductible ? `Deducible: ${data.deductible}` : '',
        data.thirdParties ? `Terceros: ${data.thirdParties}` : '',
        data.vehicleChasis ? `Chasis: ${data.vehicleChasis}` : '',
      ].filter(Boolean).join('\n'),
      claimNumber: data.claimNumber,
      policyNumber: data.policyNumber,
      company: data.company,
      clientName: data.clientName,
      clientPhone: data.clientPhone,
      clientEmail: data.clientEmail,
      clientDni: data.clientDni,
      vehicleBrand: data.vehicleBrand,
      vehicleModel: data.vehicleModel,
      vehicleYear: data.vehicleYear,
      vehiclePlate: data.vehiclePlate,
    });
    setNewModal(true);
  };

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Siniestros</h1>
          <p className="page-subtitle">{claims.length} total · {counts.open || 0} abiertos · {counts.inprogress || 0} en curso</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setPdfModal(true)}>
            <Sparkles size={14} /> Cargar denuncia con IA
          </button>
          <button className="btn btn-primary" onClick={() => { setPrefill(null); setNewModal(true); }}>
            <Plus size={15} /> Nuevo siniestro
          </button>
        </div>
      </div>

      {/* Alerta recordatorios */}
      {reminders.length > 0 && (
        <div style={{ background: 'var(--amber-dim)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Bell size={16} color="var(--amber)" style={{ flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--amber)' }}>
            <strong>🔔 {reminders.length} recordatorio{reminders.length > 1 ? 's' : ''} pendiente{reminders.length > 1 ? 's' : ''}:</strong>
            {' '}{reminders.map(r => r.title).slice(0, 3).join(' · ')}
            {reminders.length > 3 && ` y ${reminders.length - 3} más`}
          </div>
        </div>
      )}

      {/* Stats por estado — clickeables */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {Object.entries(ESTADOS).map(([k, v]) => (
          <div key={k} onClick={() => setFilterStatus(filterStatus === k ? 'all' : k)}
            className="card" style={{ flex: 1, minWidth: 100, cursor: 'pointer', borderLeft: `3px solid ${v.color}`, padding: '12px 16px',
              background: filterStatus === k ? v.bg : 'var(--bg-card)' }}>
            <div style={{ fontSize: 11, color: v.color, fontFamily: 'var(--font-display)', fontWeight: 600 }}>{v.label}</div>
            <div style={{ fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 700, color: v.color }}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por título, cliente, tipo, agente..."
            style={{ paddingLeft: 36 }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 160 }}>
          <option value="all">Todos los estados</option>
          {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ width: 150 }}>
          <option value="all">Todas las prioridades</option>
          {Object.entries(PRIORIDADES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Tabla */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', width: '100%', minWidth: 0 }}>
        <div className="table-wrap" style={{ width: '100%' }}>
          <table>
            <thead>
              <tr>
                <SortTh field="priority">Prioridad</SortTh>
                <SortTh field="client">Cliente</SortTh>
                <th>Tipo</th>
                <th>Compañía</th>
                <SortTh field="status">Estado</SortTh>
                <th>Agente</th>
                <SortTh field="reminder">Recordatorio</SortTh>
                <SortTh field="createdAt">Creado</SortTh>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const client = clients.find(cl => cl.id === c.clientId) || {};
                const linkedPolicy = c.policyId ? policies.find(p => p.id === c.policyId) : null;
                // Auditoría de compañía: si viene de una póliza vinculada, es
                // confiable. Si viene del campo extraído por IA, la validamos
                // contra la lista de aseguradoras conocidas: si no coincide
                // (suele ser el nombre del cliente o de una empresa tercera mal
                // tomado como compañía), no la mostramos y marcamos para revisar.
                const compFuente = linkedPolicy?.company || c.company || '';
                const compCanon = linkedPolicy?.company ? linkedPolicy.company : companiaCanonica(c.company);
                const compInvalida = !!compFuente && !compCanon;
                const estado = ESTADOS[c.status] || ESTADOS.open;
                const prioridad = PRIORIDADES[c.priority] || PRIORIDADES.medium;
                const hasRemAlert = c.reminder && c.reminder <= today && c.status !== 'closed';
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(c.id)}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: prioridad.color }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: prioridad.color, display: 'inline-block', flexShrink: 0 }} />
                        {prioridad.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, maxWidth: 200, fontWeight: 600 }}>{hasRemAlert && '🔔 '}{client.name || c.title || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.type || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {compCanon
                        ? compCanon
                        : compInvalida
                          ? <span title={`Valor sin reconocer: "${compFuente}". Cargá la compañía manualmente.`} style={{ color: 'var(--amber)', fontWeight: 600, cursor: 'help' }}>⚠ Cargar compañía</span>
                          : '—'}
                    </td>
                    <td>
                      <span className="badge" style={{ background: estado.bg, color: estado.color, fontSize: 11 }}>{estado.label}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.assignedTo || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {c.reminder
                        ? <span style={{ color: hasRemAlert ? 'var(--amber)' : 'var(--text-secondary)' }}>
                            {hasRemAlert && '⚠️ '}
                            {fmtPatron(c.reminder, 'dd/MM/yyyy')}
                          </span>
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {c.createdAt ? format(parseISO(c.createdAt), 'd MMM yyyy', { locale: es }) : '—'}
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setSelectedId(c.id); }}>
                        Abrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
            <AlertTriangle size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p>No hay siniestros{search ? ' con ese criterio' : ''}</p>
            {!search && (
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setNewModal(true)}>
                Registrar primer siniestro
              </button>
            )}
          </div>
        )}
      </div>

      {pdfModal && <ClaimPdfUpload onDataExtracted={handlePdfData} onClose={() => setPdfModal(false)} />}
      {newModal && <ClaimModal prefill={prefill} onClose={() => { setNewModal(false); setPrefill(null); }} />}
      {selectedId && <ClaimDetail claimId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}